import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import Typography from '@/components/ui/typography';
import { cn } from '@/lib/utils';

interface OverviewCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  className?: string;
  percentageChange?: {
    value: number;
    isPositive: boolean;
  };
}

const OverviewCard: React.FC<OverviewCardProps> = ({
  title,
  value,
  icon,
  className,
  percentageChange,
}) => {
  return (
    <Card
      className={cn(
        'bg-card-background border-card-border border-1 border-primary-base/30 hover:border-primary-base/30 hover:shadow-sm',
        className,
      )}
    >
      <CardContent className="p-4">
        <div className="flex flex-col space-y-4">
          <div className="flex items-start space-x-3 min-h-[40px]">
            {icon}

            <Typography
              variant="p"
              color="neutral600"
              weight="medium"
              size="14"
            >
              {title}
            </Typography>
          </div>
          <div className="flex items-center space-x-3">
            <Typography size="36" weight="bold" color="neutral900">
              {value}
            </Typography>
          </div>
          {percentageChange && (
            <div className="flex items-center space-x-1">
              <div
                className={cn(
                  'flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium',
                  percentageChange.isPositive
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700',
                )}
              >
                {percentageChange.isPositive ? (
                  <svg
                    className="w-3 h-3"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414l-2.293 2.293a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-3 h-3"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
                <span>{Math.abs(percentageChange.value)}%</span>
              </div>
              <Typography
                variant="small"
                color="neutral400"
                size="12"
                className="whitespace-nowrap"
              >
                vs last month
              </Typography>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default OverviewCard;
